import React, { useState } from 'react'
import { useToast } from '../../lib/ui/toast'
import Spinner from '../shared/Spinner'
import { apiPost } from '../../util/api'
import { useDropzone } from 'react-dropzone'
import { cn } from '../../lib/utils'
import { Tabs, TabsList, TabsTrigger } from '~/lib/ui/tabs'
import { useTranslation } from '~/hooks/useTranslation'

const DropzoneResumeUpload = ({ className, helpText, locale }) => {
  const { t } = useTranslation('DropzoneResumeUpload')
  const [resumeIsUploading, setResumeIsUploading] = useState(false)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [parseResume, setParseResume] = useState(
    locale == 'en_gb' ? false : true,
  )
  const { toast } = useToast()
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    accept: {
      '*/*/pdf': ['.pdf'],
      '*/*/msword': ['.doc'],
      '*/*/document': ['.docx'],
    },
    onDropAccepted: (incomingFiles) => saveNewResume(incomingFiles[0]),
    onDropRejected: (incomingFiles) => rejectionMessage(incomingFiles),
    onDragEnter: () => setIsDraggingOver(true),
    onDragLeave: () => setIsDraggingOver(false),
    onDrop: () => setIsDraggingOver(false),
    maxSize: 15_000_000, // 15MB
    disabled: resumeIsUploading,
  })

  const rejectionMessage = (incomingFiles) => {
    const {
      file: { type },
      errors,
    } = incomingFiles[0]
    const description = errors
      .map((e) => ` • ${t(e.code.replaceAll('-', '_'))}`)
      .join('\n')
    console.error('File upload rejected:', description)
    if (type) {
      toast({
        description,
        title: t('could_not_upload'),
        variant: 'destructive',
        duration: 10_000,
      })
    }
  }

  const saveNewResume = async (file) => {
    setResumeIsUploading(true)
    const name = file.name.split('.')[0]
    const lastModified = file.lastModifiedDate.toISOString()
    const formData = new FormData()
    formData.append('imported_resume', file)
    formData.append('revision_date', lastModified)
    formData.append('name', name)
    formData.append('parse_resume', parseResume)
    try {
      const response = await apiPost('/resume/builder', formData, true)
      setResumeIsUploading(false)
      toast({ description: t('uploaded_successfully') })
      if (parseResume) {
        window.location.href = `/resume/builder/${response.data.document_id}`
      } else {
        window.location.reload()
      }
    } catch (e) {
      setResumeIsUploading(false)
      console.error('Error saving new resume', e)
      toast({
        description: t('error_upload_failed'),
        variant: 'destructive',
      })
      return
    }
  }

  const baseRootClassName = cn(
    'min-h-[20rem] p-4 border-dashed border text-center content-center justify-items-center',
    className,
  )
  const defaultRootClassName = isDraggingOver
    ? baseRootClassName + ' border-primary border-2 bg-lily-blue'
    : baseRootClassName
  const rootClassName = resumeIsUploading
    ? defaultRootClassName
    : defaultRootClassName + ' cursor-pointer'

  return (
    <div className="px-4 pb-4">
      <div className="mb-4 flex flex-row items-center justify-between">
        <div className="text-lg font-medium">{t('upload_your_resume')}</div>
        <div>
          <Tabs defaultValue={parseResume} onValueChange={setParseResume}>
            <TabsList>
              <TabsTrigger value={false}>{t('no_not_import')}</TabsTrigger>
              <TabsTrigger value={true}>{t('import_into_builder')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      <div
        {...getRootProps({
          className: rootClassName,
        })}
      >
        <input {...getInputProps()} />
        {resumeIsUploading && <Spinner show={true} className="w-14" />}
        {!resumeIsUploading && (
          <div>
            <p>{helpText || t('drag_and_drop')}</p>
            <em>{t('file_invalid_type')}</em>
          </div>
        )}
      </div>
    </div>
  )
}
export default DropzoneResumeUpload
